import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swords } from 'lucide-react';
import { motion } from 'framer-motion';
import DashboardLayout from '@/components/mafia/DashboardLayout';
import { useMafiaAuth } from '@/context/MafiaAuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DashboardHistoryPage() {
  const navigate = useNavigate();
  const { gamer, loading } = useMafiaAuth();
  const [matches, setMatches] = useState([]);
  const [filter, setFilter] = useState('all');
  const [fetchLoading, setFetchLoading] = useState(true);

  useEffect(() => {
    if (!loading && !gamer) navigate('/mafia/login', { replace: true });
  }, [loading, gamer, navigate]);

  useEffect(() => {
    if (!gamer) return;
    (async () => {
      setFetchLoading(true);
      try {
        const r = await fetch(`${API}/api/mafia/auth/me/matches?limit=100`, { credentials: 'include' });
        if (r.ok) {
          const data = await r.json();
          setMatches(data.matches || []);
        }
      } finally { setFetchLoading(false); }
    })();
  }, [gamer]);

  const filtered = matches.filter((m) => {
    if (filter === 'wins') return m.won;
    if (filter === 'losses') return m.status === 'ended' && !m.won;
    return true;
  });

  if (loading || !gamer) {
    return <div className="ms-page flex items-center justify-center ms-text-3">Loading…</div>;
  }

  const FILTERS = [
    { k: 'all', l: `All (${matches.length})` },
    { k: 'wins', l: `Wins (${matches.filter(m => m.won).length})` },
    { k: 'losses', l: `Losses (${matches.filter(m => m.status === 'ended' && !m.won).length})` },
  ];

  return (
    <DashboardLayout
      title="Match History"
      subtitle="Your record"
    >
      {/* Segmented filter */}
      <div className="relative flex items-center gap-1 ms-surface-1 ms-border p-1 mb-6 w-fit" style={{ borderRadius: 'var(--ms-radius-md)' }} data-testid="history-filter">
        {FILTERS.map((opt) => (
          <button
            key={opt.k}
            onClick={() => setFilter(opt.k)}
            data-testid={`filter-${opt.k}`}
            className={`relative px-4 py-1.5 text-xs font-medium transition-colors ${filter === opt.k ? 'ms-text' : 'ms-text-2 hover:ms-text'}`}
            style={{ borderRadius: 'var(--ms-radius-sm)' }}
          >
            {filter === opt.k && (
              <motion.span
                layoutId="history-pill"
                className="absolute inset-0"
                style={{ background: 'var(--ms-surface-3)', border: '1px solid var(--ms-border-subtle)', borderRadius: 'var(--ms-radius-sm)' }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              />
            )}
            <span className="relative">{opt.l}</span>
          </button>
        ))}
      </div>

      <section className="ms-card" data-testid="history-table">
        <div className="hidden md:grid grid-cols-12 px-5 py-3 ms-border-b">
          <span className="col-span-3 ms-kicker">Date</span>
          <span className="col-span-2 ms-kicker">Seat</span>
          <span className="col-span-2 ms-kicker">Team</span>
          <span className="col-span-2 ms-kicker">State</span>
          <span className="col-span-3 ms-kicker text-right">Result</span>
        </div>
        {fetchLoading ? (
          <p className="px-5 py-12 text-sm ms-text-2 text-center">Loading matches…</p>
        ) : filtered.length === 0 ? (
          <div className="px-5 py-16 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 mx-auto mb-4" style={{ background: 'var(--ms-surface-2)', borderRadius: 'var(--ms-radius-full, 999px)' }}>
              <Swords size={20} className="ms-text-red" />
            </div>
            <p className="text-sm ms-text-2">
              {filter === 'all' ? 'No matches yet.' : `No ${filter} on record.`}
            </p>
          </div>
        ) : (
          <div className="ms-divide">
            {filtered.map((m) => (
              <div
                key={m.game_id}
                data-testid={`history-row-${m.game_id}`}
                className="grid grid-cols-2 md:grid-cols-12 px-5 py-3 hover:bg-[var(--ms-surface-2)] transition-colors items-center"
              >
                <span className="md:col-span-3 text-xs ms-text-3">
                  {m.created_at ? new Date(m.created_at).toLocaleString() : '—'}
                </span>
                <span className="md:col-span-2 text-sm ms-text">Seat #{m.seat}</span>
                <span className="md:col-span-2 text-xs ms-text-2 capitalize">{m.team || '—'}</span>
                <span className="md:col-span-2 text-xs">
                  <span className="ms-chip" style={m.alive ? { color: '#10B981' } : { color: 'var(--ms-text-muted)' }}>
                    {m.alive ? 'Alive' : 'Dead'}
                  </span>
                </span>
                <span className="md:col-span-3 text-right">
                  <span className={`ms-chip ${m.won ? 'ms-chip-red' : ''}`}>
                    {m.status === 'ended' ? (m.won ? 'Win' : 'Loss') : m.status}
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </DashboardLayout>
  );
}
